// 매도 등록/수정 화면 (통합 그리드)
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchCombinedList, fetchSummary, saveSell, deleteSell } from '../../api/sell';
import { fetchRealtimePrices } from '../../api/price';
import { fetchAccounts } from '../../api/accounts';
import { fetchExchangeRate } from '../../api/marketIndex';
import { subscribeTabNav } from '../../utils/tabNav';
import PageLayout from '../../components/PageLayout';
import StockChartPopup from '../../components/common/StockChartPopup';
import AlertButton from '../../components/common/AlertButton';
import AlertQuickPopup from '../../components/common/AlertQuickPopup';
import './SellPage.css';

const PENDING_COLOR = '#e67e22'; // 매도일자 없는 행의 단가·금액 색상 (종목명 클릭색과 동일)

const EMPTY_FORM = { sell_date: '', sell_price: '', quantity: '', memo: '', exchange_rate: '' };
const EMPTY_SUMMARY = { unsold_buy_amount: 0, unsold_profit_loss: 0, confirmed_profit_loss: 0 };

const currentYear = new Date().getFullYear();
const yearOptions = ['전체', ...Array.from({ length: 5 }, (_, i) => String(currentYear - i))];

const fNum = (v) => v != null && v !== '' ? Number(v).toLocaleString('ko-KR') : '';
const fQty = (v) => v != null && v !== '' ? Number(v).toLocaleString('ko-KR', { maximumFractionDigits: 4 }) : '';
const sign = (v) => v > 0 ? '+' : '';
const fDiff = (prevDiff, diffRate) => {
  if (prevDiff == null && diffRate == null) return '';
  const d = prevDiff != null ? `${sign(prevDiff)}${Math.round(prevDiff).toLocaleString('ko-KR')}` : '';
  const r = diffRate  != null ? `${sign(diffRate)}${Number(diffRate).toFixed(2)}%` : '';
  if (d && r) return `${d} (${r})`;
  return d || r;
};
const diffColor = (v) => v == null ? undefined : v > 0 ? '#d32f2f' : v < 0 ? '#1565c0' : undefined;
const getProfitColor = (v) => {
  if (v === null || v === undefined || v === 0) return 'inherit';
  return Number(v) > 0 ? '#d32f2f' : '#1565c0';
};

const SellPage = ({ isActive }) => {
  const [gridList,         setGridList]         = useState([]);
  const [accounts,         setAccounts]         = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState(new Set());
  const [acctDropOpen,     setAcctDropOpen]     = useState(false);
  const [showUnsold,       setShowUnsold]       = useState(true);
  const [showSold,         setShowSold]         = useState(false);
  const [sellYear,         setSellYear]         = useState(String(currentYear));
  const [inclSellPending,  setInclSellPending]  = useState(true);
  const [summary,          setSummary]          = useState(EMPTY_SUMMARY);
  const [selectedItem,     setSelectedItem]     = useState(null);
  const [editingId,        setEditingId]        = useState(null);
  const [form,             setForm]             = useState(EMPTY_FORM);
  const [loading,          setLoading]          = useState(false);
  const [message,          setMessage]          = useState({ text: '', type: '' });
  const [sortConfig,       setSortConfig]       = useState({ key: null, dir: 'asc' });
  const [chartPopup,       setChartPopup]       = useState(null);
  const [alertPopup,       setAlertPopup]       = useState(null);
  const [priceLoading,     setPriceLoading]     = useState(false);
  const [acctWidth,        setAcctWidth]        = useState(90);
  const [stockWidth,       setStockWidth]       = useState(200);
  const [buyMemoWidth,     setBuyMemoWidth]     = useState(150);
  const [sellMemoWidth,    setSellMemoWidth]    = useState(150);
  const [focusSellId,      setFocusSellId]      = useState(null);  // 다른 화면(건별수익률현황 매도금액 클릭)에서 넘어온 포커스 대상
  const [highlightSellId,  setHighlightSellId]  = useState(null);  // 포커스 강조 표시 (일시적)

  const priceAppliedRef = useRef(false);
  const acctDropRef     = useRef(null);
  const dragStartX      = useRef(0);
  const dragStartW      = useRef(0);
  const rowRefs          = useRef({});  // sell_id → <tr> DOM (포커스 스크롤용)

  // Click outside → close account dropdown
  useEffect(() => {
    const handler = (e) => {
      if (acctDropRef.current && !acctDropRef.current.contains(e.target))
        setAcctDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!isActive) return;
    fetchAccounts().then(setAccounts).catch(() => {});
  }, [isActive]);

  const accountsParam = useMemo(
    () => (selectedAccounts.size > 0 ? [...selectedAccounts].join(',') : null),
    [selectedAccounts]
  );

  const loadSummary = useCallback(async () => {
    try {
      const params = {};
      if (accountsParam) params.accounts = accountsParam;
      if (sellYear !== '전체') params.year = parseInt(sellYear);
      const res = await fetchSummary(params);
      setSummary(res);
    } catch { setSummary(EMPTY_SUMMARY); }
  }, [accountsParam, sellYear]);

  const loadGrid = useCallback(async () => {
    priceAppliedRef.current = false;
    if (!showUnsold && !showSold) { setGridList([]); return; }
    try {
      const params = { include_unsold: showUnsold, include_sold: showSold, include_sell_plan: inclSellPending };
      if (accountsParam) params.accounts = accountsParam;
      if (sellYear !== '전체') params.year = parseInt(sellYear);
      const data = await fetchCombinedList(params);
      // Stage 1: 즉시 표시 — 백엔드에서 받은 current_price로 수익률/예상손익 계산
      const enriched = data.map(item => {
        if (item.is_sold || !item.current_price || !item.buy_price) return item;
        const rate = Math.round((item.current_price - item.buy_price) / item.buy_price * 10000) / 100;
        const profit_loss = Math.round((item.current_price - item.buy_price) * item.quantity * 100) / 100;
        return { ...item, current_profit_rate: rate, profit_loss };
      });
      setGridList(enriched);
    } catch { /* silent */ }
  }, [showUnsold, showSold, accountsParam, sellYear, inclSellPending]);

  useEffect(() => {
    loadGrid();
    loadSummary();
  }, [loadGrid, loadSummary]);

  // 건별수익률현황 매도금액 클릭 등, 다른 화면에서 매도건 포커스 요청 구독
  // (멀티탭 구조라 비활성 상태에서도 항상 마운트되어 있어 언제든 수신 가능)
  // 조회조건(계좌/연도/매도여부 체크박스) 때문에 대상 레코드가 목록에서 안 보일 수 있어
  // BuyPage와 동일하게 전체 조회로 리셋한 뒤 포커스 처리
  useEffect(() => {
    const unsubscribe = subscribeTabNav(({ tab, sellId }) => {
      if (tab !== 'sell' || !sellId) return;
      setSelectedAccounts(new Set());
      setShowUnsold(true);
      setShowSold(true);
      setSellYear('전체');
      setInclSellPending(true);
      setFocusSellId(sellId);
    });
    return unsubscribe;
  }, []);

  // focusSellId 처리: 목록에 나타나면 스크롤/강조 + 우측 폼 로드
  // (매도 레코드는 매수와 달리 "완료되어 수정 불가" 상태가 없어 — SellPage는 is_sold
  //  여부와 무관하게 행 클릭 시 항상 폼을 채움 — 항상 handleRowClick 호출)
  useEffect(() => {
    if (!focusSellId) return;
    const item = gridList.find(i => i.sell_id === focusSellId);
    if (!item) return;
    handleRowClick(item);
    setHighlightSellId(focusSellId);
    setFocusSellId(null);
    const el = rowRefs.current[focusSellId];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(() => setHighlightSellId(null), 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridList, focusSellId]);

  // Stage 2: 백그라운드 실시간 시세 갱신
  useEffect(() => {
    if (priceAppliedRef.current) return;
    const unsold = gridList.filter(i => !i.is_sold);
    if (unsold.length === 0) return;
    const tickers = [...new Set(unsold.map(i => i.ticker))];
    setPriceLoading(true);
    fetchRealtimePrices(tickers)
      .then(res => {
        const map = {};
        for (const p of res.data) map[p.ticker] = p;
        priceAppliedRef.current = true;
        setGridList(prev => prev.map(item => {
          if (item.is_sold) return item;
          const p = map[item.ticker];
          if (!p || p.current_price == null || !item.buy_price) return item;
          const rate = Math.round((p.current_price - item.buy_price) / item.buy_price * 10000) / 100;
          const profit_loss = Math.round((p.current_price - item.buy_price) * item.quantity * 100) / 100;
          return {
            ...item,
            current_price:       p.current_price,
            current_profit_rate: rate,
            profit_loss,
            prev_diff:           p.prev_diff ?? item.prev_diff,
            diff_rate:           p.diff_rate ?? item.diff_rate,
          };
        }));
      })
      .catch(() => {})
      .finally(() => setPriceLoading(false));
  }, [gridList]);

  // Column resize handler factory
  const makeResizeHandler = (setter, currentWidth) => (e) => {
    e.preventDefault();
    dragStartX.current = e.clientX;
    dragStartW.current = currentWidth;
    const onMove = (ev) => {
      const delta = ev.clientX - dragStartX.current;
      setter(Math.min(400, Math.max(80, dragStartW.current + delta)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleSort = (key) => {
    setSortConfig(prev =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );
  };
  const sortArrow = (key) => sortConfig.key === key ? (sortConfig.dir === 'asc' ? ' ▲' : ' ▼') : '';

  const displayList = useMemo(() => {
    if (!sortConfig.key) return gridList;
    const dir = sortConfig.dir === 'asc' ? 1 : -1;
    return [...gridList].sort((a, b) => {
      if (sortConfig.key === 'current_profit_rate') {
        const av = a.current_profit_rate ?? null;
        const bv = b.current_profit_rate ?? null;
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return (av - bv) * dir;
      }
      return String(a[sortConfig.key] ?? '').localeCompare(String(b[sortConfig.key] ?? '')) * dir;
    });
  }, [gridList, sortConfig]);

  const handleSearch = () => {
    setSortConfig({ key: null, dir: 'asc' });
    loadGrid();
    loadSummary();
  };

  const handleClear = () => {
    setSelectedItem(null);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleRowClick = (item) => {
    setSelectedItem(item);
    if (item.sell_id != null) {
      // 기존 매도 데이터 있음 (is_sold 여부 무관) → 수정 모드
      setEditingId(item.sell_id);
      setForm({
        sell_date:  item.sell_date  || '',
        sell_price: item.sell_price ?? '',
        quantity:   item.sell_quantity ?? '',
        memo:       item.sell_memo  || '',
        exchange_rate: item.exchange_rate ?? '',
      });
    } else {
      // 매도 데이터 없음 → 신규 등록 모드
      setEditingId(null);
      setForm({ ...EMPTY_FORM, quantity: item.quantity ?? '' });
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));

    // 매도일 입력/변경 시 환율 자동조회 (휴장일이면 직전 영업일 값) — 값이 이미 있어도 항상 재조회해서 덮어씀
    if (name === 'sell_date' && value) {
      fetchExchangeRate(value)
        .then(res => {
          if (res?.rate != null) {
            setForm(prev => ({ ...prev, exchange_rate: res.rate }));
          }
        })
        .catch(() => {});
    }
  };

  // Auto-computed sell amounts
  const buyPriceVal = selectedItem?.buy_price ?? 0;
  const sellPrice   = parseFloat(form.sell_price) || 0;
  const qty         = parseFloat(form.quantity)   || 0;
  const sellAmt     = sellPrice > 0 && qty > 0 ? Math.round(sellPrice * qty * 100) / 100 : '';
  const costAmt     = buyPriceVal > 0 && qty > 0 ? buyPriceVal * qty : 0;
  const profit      = sellAmt !== '' ? sellAmt - costAmt : '';
  const profitRate  = profit !== '' && costAmt !== 0
    ? Math.round((profit / costAmt) * 10000) / 100 : '';

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  const handleSave = async () => {
    if (!selectedItem)    { showMessage('항목을 선택해주세요.', 'error'); return; }
    if (!form.sell_price) { showMessage('매도단가를 입력해주세요.', 'error'); return; }
    if (!form.quantity)   { showMessage('수량을 입력해주세요.', 'error'); return; }
    setLoading(true);
    try {
      await saveSell({
        buy_id:     selectedItem.buy_id,
        sell_id:    editingId ?? null,
        sell_date:  form.sell_date  || null,
        sell_price: parseFloat(form.sell_price),
        quantity:   parseFloat(form.quantity),
        memo:       form.memo || null,
        exchange_rate: form.exchange_rate !== '' ? parseFloat(form.exchange_rate) : null,
      });
      showMessage('매도가 저장되었습니다.');
      await loadGrid();
      await loadSummary();
      handleClear();
    } catch (err) {
      showMessage(err.response?.data?.detail || '저장에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    if (!window.confirm('이 매도 이력을 삭제하시겠습니까?\n삭제하면 해당 매수건이 미매도 상태로 복원됩니다.'))
      return;
    setLoading(true);
    try {
      await deleteSell(editingId);
      showMessage('매도 이력이 삭제되었습니다.');
      await loadGrid();
      await loadSummary();
      handleClear();
    } catch (err) {
      showMessage(err.response?.data?.detail || '삭제에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const acctLabel = selectedAccounts.size === 0
    ? '전체'
    : [...selectedAccounts].join(', ');

  const toolbar = (
    <div className="sell-toolbar">
      {/* Multi-select account dropdown */}
      <div className="sell-acct-dropdown" ref={acctDropRef}>
        <button className="sell-acct-btn" onClick={() => setAcctDropOpen(v => !v)}>
          {acctLabel} ▾
        </button>
        {acctDropOpen && (
          <div className="sell-acct-menu">
            <label className="sell-acct-item">
              <input
                type="checkbox"
                checked={selectedAccounts.size === 0}
                onChange={() => setSelectedAccounts(new Set())}
              />
              전체
            </label>
            {accounts.map(a => (
              <label key={a.id} className="sell-acct-item">
                <input
                  type="checkbox"
                  checked={selectedAccounts.has(a.account_name)}
                  onChange={() => {
                    setSelectedAccounts(prev => {
                      const next = new Set(prev);
                      if (next.has(a.account_name)) next.delete(a.account_name);
                      else next.add(a.account_name);
                      return next;
                    });
                  }}
                />
                {a.account_name}
              </label>
            ))}
          </div>
        )}
      </div>

      <label className="sell-chk-label">
        <input type="checkbox" checked={showUnsold} onChange={e => setShowUnsold(e.target.checked)} />
        미매도
      </label>
      <label className="sell-chk-label">
        <input type="checkbox" checked={showSold} onChange={e => setShowSold(e.target.checked)} />
        매도완료
      </label>

      {/* 매도기간 연도 */}
      <select
        className="sell-year-select"
        value={sellYear}
        onChange={e => setSellYear(e.target.value)}
      >
        {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
      </select>

      <button className="btn btn-primary" onClick={handleSearch}>조회</button>

      <label className="sell-chk-label">
        <input type="checkbox" checked={inclSellPending} onChange={e => setInclSellPending(e.target.checked)} />
        매도진행포함
      </label>

      {/* 합계 표시 */}
      <span className="sell-summary-text">
        미매도 매수금액:&nbsp;<strong>{fNum(Math.round(summary.unsold_buy_amount))}</strong>
        &nbsp;원&nbsp;&nbsp;|&nbsp;&nbsp;
        미매도 예상손익:&nbsp;
        <strong style={{ color: getProfitColor(summary.unsold_profit_loss) }}>
          {fNum(Math.round(summary.unsold_profit_loss))}
        </strong>
        &nbsp;원&nbsp;&nbsp;|&nbsp;&nbsp;
        매도확정손익:&nbsp;
        <strong style={{ color: getProfitColor(summary.confirmed_profit_loss) }}>
          {fNum(Math.round(summary.confirmed_profit_loss))}
        </strong>
        &nbsp;원
      </span>
    </div>
  );

  return (
    <>
      <PageLayout title="매도 등록" toolbar={toolbar} message={message}>
        <div className="sell-body">

          {/* ── 좌측 그리드 ── */}
          <div className="sell-list-panel">
            <div className="grid-wrapper">
              <table className="grid-table sell-grid">
                <thead>
                  <tr>
                    {/* 1 계좌 */}
                    <th style={{ width: acctWidth, position: 'relative', cursor: 'pointer' }} onClick={() => handleSort('account_name')}>
                      계좌{sortArrow('account_name')}
                      <span className="col-resize-handle" onMouseDown={makeResizeHandler(setAcctWidth, acctWidth)} onClick={e => e.stopPropagation()} />
                    </th>
                    {/* 2 종목명 */}
                    <th style={{ width: stockWidth, position: 'relative', cursor: 'pointer' }} onClick={() => handleSort('stock_name')}>
                      종목명{sortArrow('stock_name')}
                      <span className="col-resize-handle" onMouseDown={makeResizeHandler(setStockWidth, stockWidth)} onClick={e => e.stopPropagation()} />
                    </th>
                    {/* 4 현재가 */}
                    <th className="col-right" style={{ width: 90 }}>
                      현재가{priceLoading && <span className="price-loading-spinner" />}
                    </th>
                    {/* 5 당일등락 */}
                    <th className="col-right" style={{ width: 110 }}>당일등락</th>
                    {/* 6 차수 */}
                    <th className="col-center" style={{ width: 40 }}>차수</th>
                    {/* 6 매수일 */}
                    <th className="col-center" style={{ width: 90, cursor: 'pointer' }} onClick={() => handleSort('buy_date')}>
                      매수일{sortArrow('buy_date')}
                    </th>
                    {/* 7 매수단가 */}
                    <th className="col-right" style={{ width: 90 }}>매수단가</th>
                    {/* 8 수량 */}
                    <th className="col-right" style={{ width: 68 }}>수량</th>
                    {/* 9 매수금액 */}
                    <th className="col-right" style={{ width: 100 }}>매수금액</th>
                    {/* 10 목표수익률 */}
                    <th className="col-center" style={{ width: 60 }}>목표</th>
                    {/* 11 현재수익률 */}
                    <th className="col-center" style={{ width: 70, cursor: 'pointer' }} onClick={() => handleSort('current_profit_rate')}>
                      현재수익률{sortArrow('current_profit_rate')}
                    </th>
                    {/* 12 예상손익 */}
                    <th className="col-right" style={{ width: 90 }}>예상손익</th>
                    {/* 13 매수메모 */}
                    <th style={{ width: buyMemoWidth, position: 'relative' }}>
                      매수메모
                      <span className="col-resize-handle" onMouseDown={makeResizeHandler(setBuyMemoWidth, buyMemoWidth)} onClick={e => e.stopPropagation()} />
                    </th>
                    {/* 14 매도여부 */}
                    <th className="col-center" style={{ width: 60 }}>매도여부</th>
                    {/* 15 매도일 */}
                    <th className="col-center" style={{ width: 90, cursor: 'pointer' }} onClick={() => handleSort('sell_date')}>
                      매도일{sortArrow('sell_date')}
                    </th>
                    {/* 16 매도단가 */}
                    <th className="col-right" style={{ width: 90 }}>매도단가</th>
                    {/* 17 매도금액 */}
                    <th className="col-right" style={{ width: 100 }}>매도금액</th>
                    {/* 18 확정손익 */}
                    <th className="col-right" style={{ width: 100 }}>확정손익</th>
                    {/* 19 확정수익률 */}
                    <th className="col-center" style={{ width: 70 }}>확정수익률</th>
                    {/* 20 매도후증감 */}
                    <th className="col-center" style={{ width: 70 }}>매도후증감</th>
                    {/* 21 매도메모 */}
                    <th style={{ width: sellMemoWidth, position: 'relative' }}>
                      매도메모
                      <span className="col-resize-handle" onMouseDown={makeResizeHandler(setSellMemoWidth, sellMemoWidth)} onClick={e => e.stopPropagation()} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayList.length === 0 ? (
                    <tr><td colSpan={21} className="grid-empty">데이터가 없습니다.</td></tr>
                  ) : (
                    displayList.map((item) => {
                      const isSelected = selectedItem?.buy_id === item.buy_id &&
                        (selectedItem?.sell_id ?? null) === (item.sell_id ?? null);
                      return (
                        <tr
                          key={`${item.buy_id}-${item.sell_id ?? 'n'}`}
                          ref={el => { if (item.sell_id != null) rowRefs.current[item.sell_id] = el; }}
                          onClick={() => handleRowClick(item)}
                          className={`${item.is_sold ? 'row-sold' : ''} ${isSelected ? 'selected' : ''} ${highlightSellId === item.sell_id ? 'row-focus-highlight' : ''}`}
                        >
                          {/* 1 계좌 */}
                          <td>{item.account_name}</td>
                          {/* 2 종목명 */}
                          <td className="col-ellipsis" style={{ maxWidth: stockWidth }}>
                            <span
                              className="stock-chart-link"
                              onClick={e => {
                                e.stopPropagation();
                                setChartPopup({
                  ticker: item.ticker,
                  stockName: item.stock_name || item.ticker,
                  accountName: item.account_name,
                  currentPrice: item.current_price || null,
                  diffRate: item.diff_rate || null,
                  turns: item.buy_price > 0 ? [{ turn_no: item.turn_no || 1, buy_date: item.buy_date || null, buy_price: item.buy_price, quantity: item.quantity, profit_rate: item.current_profit_rate || null }] : [],
                });
                              }}
                            >
                              {item.stock_name || '-'}
                            </span>
                            {!item.is_sold && (() => {
                              const useSell    = item.sell_id != null;
                              const hasAlert   = useSell
                                ? (item.sell_alert_up != null || item.sell_alert_down != null)
                                : (item.buy_alert_up  != null || item.buy_alert_down  != null);
                              const alertEnabled = useSell
                                ? !!item.sell_alert_enabled
                                : !!item.buy_alert_enabled;
                              const popupExtra = useSell
                                ? { sellId: item.sell_id }
                                : { buyId:  item.buy_id  };
                              const typeNameUp   = useSell ? item.sell_alert_type_name_up   : item.buy_alert_type_name_up;
                              const typeNameDown = useSell ? item.sell_alert_type_name_down : item.buy_alert_type_name_down;
                              return (
                                <AlertButton
                                  hasAlert={hasAlert}
                                  alertEnabled={alertEnabled}
                                  alertTypeNameUp={typeNameUp}
                                  alertTypeNameDown={typeNameDown}
                                  onClick={() => setAlertPopup({ ...popupExtra, ticker: item.ticker, stockName: item.stock_name, accountName: item.account_name, turnNo: item.turn_no })}
                                />
                              );
                            })()}
                          </td>
                          {/* 4 현재가 */}
                          <td className="col-right">{fNum(item.current_price)}</td>
                          {/* 5 당일등락 */}
                          <td className="col-right" style={{ color: !item.is_sold ? diffColor(item.diff_rate) : undefined }}>
                            {!item.is_sold ? fDiff(item.prev_diff, item.diff_rate) : ''}
                          </td>
                          {/* 6 차수 */}
                          <td className="col-center">{item.turn_no}</td>
                          {/* 6 매수일 */}
                          <td className="col-center">{item.buy_date || ''}</td>
                          {/* 7 매수단가 */}
                          <td className="col-right" style={!item.buy_date ? { color: PENDING_COLOR } : undefined}>{fNum(item.buy_price)}</td>
                          {/* 8 수량 */}
                          <td className="col-right" style={!item.buy_date ? { color: PENDING_COLOR } : undefined}>{fQty(item.quantity)}</td>
                          {/* 9 매수금액 */}
                          <td className="col-right" style={!item.buy_date ? { color: PENDING_COLOR } : undefined}>{fNum(item.buy_amount)}</td>
                          {/* 10 목표수익률 */}
                          <td className="col-center">
                            {item.target_profit_rate != null ? `${item.target_profit_rate}%` : ''}
                          </td>
                          {/* 11 현재수익률 */}
                          <td
                            className="col-center"
                            style={{
                              color: !item.is_sold ? getProfitColor(item.current_profit_rate) : 'inherit',
                              fontWeight: !item.is_sold && item.current_profit_rate ? 600 : 'normal',
                            }}
                          >
                            {!item.is_sold && item.current_profit_rate != null
                              ? `${parseFloat(item.current_profit_rate).toFixed(2)}%`
                              : ''}
                          </td>
                          {/* 12 예상손익 */}
                          <td
                            className="col-right"
                            style={{ color: !item.is_sold ? getProfitColor(item.profit_loss) : 'inherit' }}
                          >
                            {!item.is_sold && item.profit_loss != null
                              ? fNum(Math.round(item.profit_loss))
                              : ''}
                          </td>
                          {/* 13 매수메모 */}
                          <td className="col-ellipsis">{item.buy_memo || ''}</td>
                          {/* 14 매도여부 */}
                          <td className="col-center">
                            {item.is_sold
                              ? <span className="badge-sold">매도완료</span>
                              : <span className="badge-hold">미매도</span>}
                          </td>
                          {/* 15 매도일 */}
                          <td className="col-center">{item.sell_date || ''}</td>
                          {/* 16 매도단가 */}
                          <td className="col-right" style={!item.sell_date ? { color: PENDING_COLOR } : undefined}>{fNum(item.sell_price)}</td>
                          {/* 17 매도금액 */}
                          <td className="col-right" style={!item.sell_date ? { color: PENDING_COLOR } : undefined}>{fNum(item.sell_amount)}</td>
                          {/* 18 확정손익 */}
                          <td
                            className="col-right"
                            style={{ color: getProfitColor(item.confirmed_profit_loss) }}
                          >
                            {item.confirmed_profit_loss != null ? fNum(Math.round(item.confirmed_profit_loss)) : ''}
                          </td>
                          {/* 19 확정수익률 */}
                          <td
                            className="col-center"
                            style={{ color: getProfitColor(item.confirmed_profit_rate) }}
                          >
                            {item.confirmed_profit_rate != null ? `${Number(item.confirmed_profit_rate).toFixed(2)}%` : ''}
                          </td>
                          {/* 20 매도후증감 */}
                          <td
                            className="col-center"
                            style={{ color: getProfitColor(item.after_sell_change_rate) }}
                          >
                            {item.after_sell_change_rate != null ? `${Number(item.after_sell_change_rate).toFixed(2)}%` : ''}
                          </td>
                          {/* 21 매도메모 */}
                          <td className="col-ellipsis">{item.sell_memo || ''}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── 우측 폼 ── */}
          <div className="sell-form-panel">
            <h3 className="sell-form-title">
              {editingId === null ? '매도 등록' : '매도 수정'}
            </h3>

            <div className="sf-section-label">매수 정보</div>
            <div className="sf-row">
              <label>계좌</label>
              <input type="text" value={selectedItem?.account_name || ''} readOnly className="input-readonly" />
            </div>
            <div className="sf-row">
              <label>종목</label>
              <input
                type="text"
                value={selectedItem ? `${selectedItem.stock_name || ''} (${selectedItem.ticker})` : ''}
                readOnly
                className="input-readonly"
              />
            </div>
            <div className="sf-row">
              <label>차수</label>
              <input
                type="text"
                value={selectedItem ? `${selectedItem.turn_no}차 (${selectedItem.ticker})` : ''}
                readOnly
                className="input-readonly"
              />
            </div>
            <div className="sf-row">
              <label>매수일</label>
              <input type="text" value={selectedItem?.buy_date || ''} readOnly className="input-readonly" />
            </div>
            <div className="sf-row">
              <label>매수단가</label>
              <input type="text" value={fNum(selectedItem?.buy_price)} readOnly className="input-readonly" />
            </div>
            <div className="sf-row">
              <label>매수환율</label>
              <input type="text" value={fNum(selectedItem?.buy_exchange_rate)} readOnly className="input-readonly" />
            </div>
            <div className="sf-row">
              <label>매수금액</label>
              <input type="text" value={fNum(selectedItem?.buy_amount)} readOnly className="input-readonly" />
            </div>
            <div className="sf-row sf-row-memo">
              <label>매수메모</label>
              <textarea
                value={selectedItem?.buy_memo || ''}
                readOnly
                className="input-readonly"
                rows={2}
              />
            </div>

            <div className="sf-section-label">매도 정보</div>
            <div className="sf-row">
              <label>매도일</label>
              <input type="date" name="sell_date" value={form.sell_date} onChange={handleFormChange} />
            </div>
            <div className="sf-row">
              <label>
                환율
                <span className="sf-help" title="매도일 기준 원달러 환율이 자동으로 채워집니다. 직접 수정할 수 있습니다.">?</span>
              </label>
              <input
                type="number"
                name="exchange_rate"
                value={form.exchange_rate}
                onChange={handleFormChange}
                min={0}
                step="any"
                placeholder="매도일 입력 시 자동조회"
              />
            </div>
            <div className="sf-row">
              <label>매도단가 <span className="required">*</span></label>
              <input
                type="number"
                name="sell_price"
                value={form.sell_price}
                onChange={handleFormChange}
                min={0}
                step="any"
                placeholder="0"
              />
            </div>
            <div className="sf-row">
              <label>수량 <span className="required">*</span></label>
              <input
                type="number"
                name="quantity"
                value={form.quantity}
                onChange={handleFormChange}
                min={0}
                step="any"
                placeholder="0"
              />
            </div>
            <div className="sf-row">
              <label>매도금액</label>
              <input
                type="text"
                value={sellAmt !== '' ? fNum(sellAmt) : ''}
                readOnly
                className="input-readonly"
                placeholder="자동계산"
              />
            </div>
            <div className="sf-row">
              <label>확정손익</label>
              <input
                type="text"
                value={profit !== '' ? fNum(Math.round(profit)) : ''}
                readOnly
                className="input-readonly"
                style={profit !== '' ? { color: getProfitColor(profit) } : {}}
                placeholder="자동계산"
              />
            </div>
            <div className="sf-row">
              <label>확정수익률</label>
              <input
                type="text"
                value={profitRate !== '' ? `${profitRate}%` : ''}
                readOnly
                className="input-readonly"
                style={profitRate !== '' ? { color: getProfitColor(profitRate) } : {}}
                placeholder="자동계산"
              />
            </div>
            <div className="sf-row sf-row-memo">
              <label>매도메모</label>
              <textarea
                name="memo"
                value={form.memo}
                onChange={handleFormChange}
                maxLength={1000}
                rows={2}
                placeholder="메모 (선택)"
              />
            </div>

            <div className="sf-actions">
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={loading || !selectedItem}
              >
                {loading ? '저장 중...' : '저장'}
              </button>
              {editingId !== null && (
                <button className="btn btn-danger" onClick={handleDelete} disabled={loading}>
                  삭제
                </button>
              )}
              <button className="btn btn-secondary" onClick={handleClear} disabled={loading}>
                취소
              </button>
            </div>
          </div>

        </div>
      </PageLayout>

      {chartPopup && (
        <StockChartPopup
          ticker={chartPopup.ticker}
          stockName={chartPopup.stockName}
          accountName={chartPopup.accountName}
          currentPrice={chartPopup.currentPrice}
          diffRate={chartPopup.diffRate}
          turns={chartPopup.turns || []}
          onClose={() => setChartPopup(null)}
        />
      )}
      {alertPopup && (
        <AlertQuickPopup
          buyId={alertPopup.buyId}
          sellId={alertPopup.sellId}
          ticker={alertPopup.ticker}
          stockName={alertPopup.stockName}
          accountName={alertPopup.accountName}
          turnNo={alertPopup.turnNo}
          onClose={() => setAlertPopup(null)}
          onSaved={() => { loadGrid(); loadSummary(); }}
        />
      )}
    </>
  );
};

export default SellPage;
